import { useSyncExternalStore } from 'react'
import {
  readKnownStorefront,
  subscribeStorefront,
  type StorefrontIdentity,
} from '@/features/clubs/storefront'
import { storefrontSlugFor } from '@/app/router/resolveChrome'

/**
 * The storefront identity for the pathname the shell is rendering, or `null`.
 *
 * The shell sits ABOVE the route, so it cannot read the club page's state — it reads the
 * store every public club read writes (`features/clubs/storefront.ts`) and re-renders
 * when the answer lands. Arriving from the directory or the home page, the answer is
 * already there; on a cold open of `/clubs/<slug>` the ordinary player chrome renders
 * first and gives way once the club's own read resolves. That order is deliberate: the
 * fallback while nothing is known must be the surface every club has today.
 *
 * `null` now means one thing only — nobody has read a club under this slug, or the club
 * read had no slug to be keyed by. It used to mean "and the club has not paid" as well;
 * the entitlement is gone (2026-09-07, `features/clubs/storefront.ts`) and the waiting
 * behaviour above is untouched by its removal, because it never depended on it.
 */
export function useStorefrontClub(pathname: string): StorefrontIdentity | null {
  const slug = storefrontSlugFor(pathname)
  return useSyncExternalStore(subscribeStorefront, () =>
    slug ? readKnownStorefront(slug) : null,
  )
}
